'use client'

import { useEffect, useState } from 'react'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/shared/api-client'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { FeedbackFormModal } from '@marketplace-web/shared/ui-helpers'
import { isStringTruthy } from '@marketplace-web/shared/utils'
import { clickEvent, viewScreenEvent } from '_libs/common/event-tracker/events'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'

import {
  getOfflineVerificationFeedback,
  sendOfflineVerificationFeedback,
  transformFeedbackRatingsResponse,
  UpdateFeedbackRatingOptionsArgs,
} from '@marketplace-web/domain/conversation-feedback-data'

import { removeFeedbackUrlParam } from './utils'

const OfflineVerificationFeedback = () => {
  const { relativeUrl, searchParams, replaceHistoryState, urlQuery } = useLocation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { transaction } = useConversationContext()
  const { track } = useTracking()

  const { offline_verification_feedback_seller } = searchParams
  const showFeedback = isStringTruthy(offline_verification_feedback_seller)
  const urlWithoutFeedback = removeFeedbackUrlParam(relativeUrl, urlQuery)

  const { transformedData: feedbackRatings, fetch: fetchOfflineVerificationFeedback } = useFetch(
    getOfflineVerificationFeedback,
    transformFeedbackRatingsResponse,
  )

  useEffect(() => {
    if (!showFeedback) return

    replaceHistoryState(urlWithoutFeedback)
  }, [showFeedback, replaceHistoryState, urlWithoutFeedback])

  useEffect(() => {
    if (!feedbackRatings) return

    setIsModalOpen(true)
    track(viewScreenEvent({ screen: Screen.OfflineVerificationFeedbackForm }))
  }, [setIsModalOpen, feedbackRatings, track])

  useEffect(() => {
    if (!showFeedback || !transaction?.id) return

    fetchOfflineVerificationFeedback(transaction.id)
  }, [showFeedback, transaction, fetchOfflineVerificationFeedback])

  const handleModalClose = () => {
    setIsModalOpen(false)

    track(
      clickEvent({
        screen: Screen.OfflineVerificationFeedbackForm,
        target: ClickableElement.CloseScreen,
      }),
    )
  }

  const handleFeedbackUpdate = async ({
    rating,
    message,
    feedbackStatementIds,
    finalized,
  }: UpdateFeedbackRatingOptionsArgs) => {
    if (!transaction?.id) return
    if (!finalized) {
      await sendOfflineVerificationFeedback({ transactionId: transaction.id, rating })

      return
    }

    setIsModalOpen(false)

    await sendOfflineVerificationFeedback({
      transactionId: transaction.id,
      rating,
      comment: message,
      feedbackStatementIds,
      finalized,
    })
  }

  if (!isModalOpen) return null

  return (
    <FeedbackFormModal
      isOpen
      ratings={feedbackRatings}
      onClose={handleModalClose}
      onSubmit={handleFeedbackUpdate}
      isCloseActionVisible={false}
      isInputWithoutRatingOptions
    />
  )
}

export default OfflineVerificationFeedback
